import { Pipe, PipeTransform } from "@angular/core";

/** One row of a parsed lineage tree. */
export interface LineageNode {
  depth: number;
  name: string;
  /** The job the lineage was requested for (was `**bold**` / `★`). */
  isTarget: boolean;
  /** Checkpoint number the job was spawned from, if present. */
  cp: number | null;
}

/**
 * Parses the ascii lineage tree (a markdown code-fence string of `└──`-indented
 * lines with `**bold**` targets, `★`, and `(cp: N)`) into structured nodes for
 * styled rendering — instead of dumping it through the markdown pipe as a code block.
 *
 * Pure pipe: Angular memoizes by input reference, so it only re-runs when the
 * raw string changes.
 */
@Pipe({
  name: "lineageTree",
  standalone: true,
})
export class LineageTreePipe implements PipeTransform {
  transform(raw: string | null | undefined): LineageNode[] {
    if (!raw) return [];

    return raw
      .split("\n")
      .map((l) => l.replace(/\r$/, ""))
      .filter((l) => l.trim() && !l.trim().startsWith("```"))
      .map((line) => {
        const indent = line.length - line.trimStart().length;
        const text = line.trimStart().replace(/^[└├]──\s*/, "");
        const cpMatch = text.match(/\(cp:\s*(\d+)\)/);
        const isTarget = text.includes("**") || text.includes("★");
        const name = text
          .replace(/\*\*/g, "")
          .replace(/★/g, "")
          .replace(/\(cp:\s*\d+\)/, "")
          .trim();
        return {
          depth: Math.round(indent / 4),
          name,
          isTarget,
          cp: cpMatch ? Number(cpMatch[1]) : null,
        };
      });
  }
}
